import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  of,
  throwError
} from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { MEGA_AUTH_STORAGE_KEY } from '../constants/auth-session.constants';
import {
  MEGACABLE_ACCESS_TOKEN_KEY,
  MEGACABLE_CREDIT_ENABLE_KEY,
  MEGACABLE_EXPIRES_AT_KEY,
  MEGACABLE_INSTALLATION_DATE_KEY,
  MEGACABLE_PROFILE_KEY,
  MEGACABLE_TOKEN_TYPE_KEY,
  MEGACABLE_SWAGGER_TOKEN_HEADER
} from '../constants/megacable-auth.constants';
import {
  MegacableAddress,
  MegacableCustomerProfile
} from '../models/megacable-customer-profile.model';
import { MegacableLogoutResponse } from '../models/megacable-logout.model';
import {
  MegacableLoginError,
  MegacableLoginRequest,
  MegacableLoginResponse
} from '../models/megacable-login.model';

@Injectable({
  providedIn: 'root'
})
export class MegacableAuthService {

  private readonly _loginUrl = `${environment.API_SECURITY}/Megacable/login`;
  private readonly _creditoUrl = `${environment.API_SECURITY}/Megacable/credito`;
  private readonly _logoutUrl = `${environment.API_SECURITY}/Megacable/logout`;

  private readonly _sessionChange$ = new BehaviorSubject<void>(undefined);
  private readonly _profile$ = new BehaviorSubject<MegacableCustomerProfile | null>(null);

  private _profileRequest$?: Observable<MegacableCustomerProfile>;

  readonly sessionChange$ = this._sessionChange$.asObservable();
  readonly profile$ = this._profile$.asObservable();

  constructor(private readonly _http: HttpClient) {
    this._hydrateProfileFromSession();
  }

  login(credentials: MegacableLoginRequest): Observable<MegacableLoginResponse> {
    const payload: MegacableLoginRequest = {
      username: `${credentials.username ?? ''}`.trim(),
      password: credentials.password ?? ''
    };

    return this._http.post<unknown>(this._loginUrl, payload).pipe(
      map((response) => this._normalizeLoginResponse(response)),
      tap((response) => this._persistLoginSession(response)),
      catchError((error) => throwError(() => this._mapLoginError(error)))
    );
  }

  /** Login + perfil (misma secuencia que la página de login). */
  signInAndLoadProfile(
    credentials: MegacableLoginRequest
  ): Observable<MegacableCustomerProfile> {
    return this.login(credentials).pipe(
      switchMap(() => this.getCustomerProfile())
    );
  }

  getCustomerProfile(): Observable<MegacableCustomerProfile> {
    const cached = this._profile$.getValue();
    if (cached) {
      return of(cached);
    }

    if (!this.isAuthenticated()) {
      return throwError(() => new MegacableLoginError('Sesión no válida.', 401));
    }

    if (!this._profileRequest$) {
      this._profileRequest$ = this._http.post<unknown>(this._creditoUrl, {}).pipe(
        map((response) => this._normalizeCustomerProfile(response)),
        tap((profile) => this._setProfile(profile)),
        catchError((error) => {
          this._profileRequest$ = undefined;
          return this._handleProfileError(error);
        }),
        shareReplay(1)
      );
    }

    return this._profileRequest$;
  }

  /** Restaura perfil en memoria y consulta crédito si hay token sin perfil. */
  restoreSession(): void {
    this._hydrateProfileFromSession();

    if (!this.isAuthenticated()) {
      return;
    }

    if (this._profile$.getValue()) {
      this._sessionChange$.next();
      return;
    }

    this.getCustomerProfile().subscribe({
      error: () => {
        /* manejo centralizado en _handleProfileError */
      }
    });
  }

  getProfile(): MegacableCustomerProfile | null {
    return this._profile$.getValue();
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  getToken(): string | null {
    try {
      const token = sessionStorage.getItem(MEGACABLE_ACCESS_TOKEN_KEY);
      if (!token?.trim()) {
        return null;
      }
      return this._normalizeJwtToken(token);
    } catch {
      return null;
    }
  }

  /** JWT listo para headers Megacable (sin prefijo Bearer). */
  getAuthHeaderValue(): string | null {
    return this.getToken();
  }

  /** Header `Authorization: Bearer` usando el tokenType de login. */
  getAuthorizationHeaderValue(): string | null {
    const jwt = this.getToken();
    if (!jwt) {
      return null;
    }

    const tokenType = this.getTokenType();
    return `${tokenType} ${jwt}`;
  }

  getTokenType(): string {
    try {
      return sessionStorage.getItem(MEGACABLE_TOKEN_TYPE_KEY)?.trim() || 'Bearer';
    } catch {
      return 'Bearer';
    }
  }

  /** `enableCredit` de POST /Megacable/credito; fallback al valor de sesión hasta cargar perfil. */
  isCreditEnabled(): boolean {
    const profile = this._profile$.getValue();
    if (profile != null && Object.prototype.hasOwnProperty.call(profile, 'enableCredit')) {
      return profile.enableCredit;
    }

    try {
      return sessionStorage.getItem(MEGACABLE_CREDIT_ENABLE_KEY) === 'true';
    } catch {
      return false;
    }
  }

  getExpiresAt(): number | null {
    try {
      const raw = sessionStorage.getItem(MEGACABLE_EXPIRES_AT_KEY);
      if (raw == null || raw === '') {
        return null;
      }
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    } catch {
      return null;
    }
  }

  /** `installationDate` de POST /Megacable/credito (sessionStorage). */
  getInstallationDate(): string {
    try {
      const fromKey = sessionStorage.getItem(MEGACABLE_INSTALLATION_DATE_KEY);
      if (fromKey != null && `${fromKey}`.trim() !== '') {
        return `${fromKey}`;
      }
    } catch {
      /* ignore */
    }

    const fromProfile = this._profile$.getValue()?.installationDate;
    return fromProfile != null && `${fromProfile}`.trim() !== '' ? `${fromProfile}` : '';
  }

  logoutRemote(): Observable<MegacableLogoutResponse> {
    const jwt = this.getAuthHeaderValue();
    if (!jwt) {
      return of({ revoked: true });
    }

    return this._http
      .post<unknown>(this._logoutUrl, null, {
        headers: this._buildLogoutHeaders(jwt)
      })
      .pipe(
        map((response) => this._normalizeLogoutResponse(response)),
        catchError((error: HttpErrorResponse) => {
          if (error?.status === 401) {
            return of({ revoked: true });
          }
          return throwError(() => error);
        })
      );
  }

  /**
   * Cierra sesión en Megacable y limpia almacenamiento local.
   * Ante 401, 5xx o timeout siempre limpia la sesión local.
   */
  logout(): Observable<void> {
    return this.logoutRemote().pipe(
      map(() => {
        this.clearSession();
      }),
      catchError(() => {
        this.clearSession();
        return of(undefined);
      })
    );
  }

  clearSession(): void {
    try {
      sessionStorage.removeItem(MEGACABLE_ACCESS_TOKEN_KEY);
      sessionStorage.removeItem(MEGACABLE_TOKEN_TYPE_KEY);
      sessionStorage.removeItem(MEGACABLE_CREDIT_ENABLE_KEY);
      sessionStorage.removeItem(MEGACABLE_EXPIRES_AT_KEY);
      sessionStorage.removeItem(MEGACABLE_PROFILE_KEY);
      sessionStorage.removeItem(MEGACABLE_INSTALLATION_DATE_KEY);
      localStorage.removeItem(MEGA_AUTH_STORAGE_KEY);
      sessionStorage.removeItem('mega_cart_guest');
    } catch {
      /* ignore */
    }

    this._profile$.next(null);
    this._profileRequest$ = undefined;
    this._sessionChange$.next();
  }

  private _buildLogoutHeaders(jwt: string): HttpHeaders {
    const authorization = this.getAuthorizationHeaderValue() ?? `Bearer ${jwt}`;

    return new HttpHeaders({
      Authorization: authorization,
      [MEGACABLE_SWAGGER_TOKEN_HEADER]: jwt
    });
  }

  private _setProfile(profile: MegacableCustomerProfile): void {
    try {
      sessionStorage.setItem(MEGACABLE_PROFILE_KEY, JSON.stringify(profile));
      sessionStorage.setItem(MEGACABLE_CREDIT_ENABLE_KEY, String(profile.enableCredit));
      if (profile.installationDate != null && `${profile.installationDate}`.trim() !== '') {
        sessionStorage.setItem(
          MEGACABLE_INSTALLATION_DATE_KEY,
          `${profile.installationDate}`
        );
      } else {
        sessionStorage.removeItem(MEGACABLE_INSTALLATION_DATE_KEY);
      }
    } catch {
      /* ignore */
    }
    this._profile$.next(profile);
    this._sessionChange$.next();
  }

  private _hydrateProfileFromSession(): void {
    if (this._profile$.getValue()) {
      return;
    }

    try {
      const raw = sessionStorage.getItem(MEGACABLE_PROFILE_KEY);
      if (!raw?.trim()) {
        return;
      }
      const parsed = JSON.parse(raw) as MegacableCustomerProfile;
      if (
        parsed?.firstName?.trim() &&
        Object.prototype.hasOwnProperty.call(parsed, 'enableCredit')
      ) {
        const profile = this._normalizeCustomerProfile(parsed);
        this._profile$.next(profile);
        if (profile.installationDate?.trim()) {
          sessionStorage.setItem(
            MEGACABLE_INSTALLATION_DATE_KEY,
            profile.installationDate
          );
        }
      }
    } catch {
      sessionStorage.removeItem(MEGACABLE_PROFILE_KEY);
    }
  }

  private _persistLoginSession(response: MegacableLoginResponse): void {
    try {
      sessionStorage.setItem(
        MEGACABLE_ACCESS_TOKEN_KEY,
        this._normalizeJwtToken(response.accessToken)
      );
      sessionStorage.setItem(MEGACABLE_TOKEN_TYPE_KEY, response.tokenType);
      sessionStorage.setItem(MEGACABLE_CREDIT_ENABLE_KEY, String(response.creditEnable));
      sessionStorage.setItem(MEGACABLE_EXPIRES_AT_KEY, String(response.expiresAt));
      sessionStorage.removeItem(MEGACABLE_PROFILE_KEY);
      sessionStorage.removeItem(MEGACABLE_INSTALLATION_DATE_KEY);
    } catch {
      /* ignore */
    }
    this._profile$.next(null);
    this._profileRequest$ = undefined;
    this._sessionChange$.next();
  }

  private _handleProfileError(error: HttpErrorResponse): Observable<never> {
    if (error?.status === 401) {
      this.clearSession();
    }
    return throwError(
      () =>
        new MegacableLoginError(
          'No fue posible obtener tu información. Inténtalo nuevamente.',
          error?.status
        )
    );
  }

  private _normalizeCustomerProfile(response: unknown): MegacableCustomerProfile {
    const raw =
      response && typeof response === 'object'
        ? (response as Record<string, unknown>)
        : {};

    const addressRaw = raw['address'] ?? raw['Address'];

    return {
      reconnections12m: this._readNumber(raw, 'reconnections12m', 'Reconnections12m') ?? 0,
      reconnections6m: this._readNumber(raw, 'reconnections6m', 'Reconnections6m') ?? 0,
      reconnections3m: this._readNumber(raw, 'reconnections3m', 'Reconnections3m') ?? 0,
      currentMonthlyAmount:
        this._readNumber(raw, 'currentMonthlyAmount', 'CurrentMonthlyAmount') ?? 0,
      billingCutoffDate: this._readString(
        raw,
        'billingCutoffDate',
        'BillingCutoffDate'
      ),
      firstName: this._readString(raw, 'firstName', 'FirstName'),
      lastName: this._readString(raw, 'lastName', 'LastName'),
      secondLastName: this._readString(raw, 'secondLastName', 'SecondLastName'),
      email: this._readString(raw, 'email', 'Email'),
      mobilePhone: this._readString(raw, 'mobilePhone', 'MobilePhone'),
      enableCredit: this._readBoolean(raw, 'enableCredit', 'EnableCredit'),
      installationDate: this._readInstallationDate(raw),
      address: this._normalizeAddress(addressRaw)
    };
  }

  private _readInstallationDate(source: Record<string, unknown>): string {
    for (const key of ['installationDate', 'InstallationDate']) {
      const value = source[key];
      if (value == null) {
        continue;
      }

      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return `${value}`;
      }
    }

    return '';
  }

  private _normalizeAddress(addressRaw: unknown): MegacableAddress {
    const raw =
      addressRaw && typeof addressRaw === 'object'
        ? (addressRaw as Record<string, unknown>)
        : {};

    return {
      street: this._readString(raw, 'street', 'Street'),
      externalNumber: this._readString(raw, 'externalNumber', 'ExternalNumber'),
      internalNumber: this._readString(raw, 'internalNumber', 'InternalNumber'),
      neighborhood: this._readString(raw, 'neighborhood', 'Neighborhood'),
      municipality: this._readString(raw, 'municipality', 'Municipality'),
      state: this._readString(raw, 'state', 'State'),
      zipCode: this._readString(raw, 'zipCode', 'ZipCode'),
      reference: this._readString(raw, 'reference', 'Reference'),
      betweenStreets: this._readString(raw, 'betweenStreets', 'BetweenStreets'),
      deliveryInstructions: this._readString(
        raw,
        'deliveryInstructions',
        'DeliveryInstructions'
      )
    };
  }

  private _normalizeLogoutResponse(response: unknown): MegacableLogoutResponse {
    const raw =
      response && typeof response === 'object'
        ? (response as Record<string, unknown>)
        : {};

    return {
      revoked: this._readBoolean(raw, 'revoked', 'Revoked')
    };
  }

  private _normalizeLoginResponse(response: unknown): MegacableLoginResponse {
    const raw =
      response && typeof response === 'object'
        ? (response as Record<string, unknown>)
        : {};

    const accessToken = this._readString(raw, 'accessToken', 'AccessToken');
    if (!accessToken) {
      throw new MegacableLoginError(
        'No fue posible iniciar sesión. Inténtalo nuevamente.',
        0
      );
    }

    const tokenType = this._readString(raw, 'tokenType', 'TokenType') || 'Bearer';
    const creditEnable = this._readBoolean(raw, 'creditEnable', 'CreditEnable');
    const expiresAt = this._readNumber(raw, 'expiresAt', 'ExpiresAt') ?? 0;

    return {
      creditEnable,
      tokenType,
      accessToken,
      expiresAt
    };
  }

  private _mapLoginError(error: unknown): MegacableLoginError {
    const httpError = error instanceof HttpErrorResponse ? error : null;
    const status =
      httpError?.status ?? (error as { status?: number } | undefined)?.status;
    const body = httpError?.error ?? error;
    const apiDetail = this._extractApiErrorDetail(body);

    if (apiDetail) {
      return new MegacableLoginError(apiDetail, status);
    }

    if (status === 503) {
      return new MegacableLoginError(
        'Servicio Megacable temporalmente no disponible. Inténtalo nuevamente más tarde.',
        503
      );
    }

    return new MegacableLoginError(
      'No fue posible iniciar sesión. Inténtalo nuevamente.',
      status
    );
  }

  /**
   * Mensaje de error de la API: `detail` en raíz o dentro de `apiError`
   * (p. ej. { statusCode: 401, apiError: { code, detail } }).
   */
  private _extractApiErrorDetail(body: unknown): string {
    if (!body || typeof body !== 'object') {
      return '';
    }

    const raw = body as Record<string, unknown>;
    const direct = this._readString(raw, 'detail', 'Detail');
    if (direct) {
      return direct;
    }

    const apiError = raw['apiError'] ?? raw['ApiError'];
    if (apiError && typeof apiError === 'object') {
      return this._readString(
        apiError as Record<string, unknown>,
        'detail',
        'Detail'
      );
    }

    return '';
  }

  private _readString(
    source: Record<string, unknown>,
    ...keys: string[]
  ): string {
    for (const key of keys) {
      const value = source[key];
      if (value != null && `${value}`.trim() !== '') {
        return `${value}`.trim();
      }
    }
    return '';
  }

  private _readBoolean(
    source: Record<string, unknown>,
    ...keys: string[]
  ): boolean {
    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'boolean') {
        return value;
      }
      if (value === 'true' || value === '1') {
        return true;
      }
      if (value === 'false' || value === '0') {
        return false;
      }
    }
    return false;
  }

  private _readNumber(
    source: Record<string, unknown>,
    ...keys: string[]
  ): number | null {
    for (const key of keys) {
      const value = source[key];
      if (value == null || value === '') {
        continue;
      }
      const n = Number(value);
      if (Number.isFinite(n)) {
        return n;
      }
    }
    return null;
  }

  /** Asegura enviar únicamente el JWT en headers Megacable. */
  private _normalizeJwtToken(value: string): string {
    let token = `${value ?? ''}`.trim();

    if (
      (token.startsWith('"') && token.endsWith('"')) ||
      (token.startsWith("'") && token.endsWith("'"))
    ) {
      token = token.slice(1, -1).trim();
    }

    token = token.replace(/^Bearer\s+/i, '').trim();
    return token;
  }
}
